import {Inject, Injectable, InjectionToken} from "@angular/core";
import {JwtHttpClientService} from "../../http/jwt-http-client.service";
import {ConfigurationService} from "../../configuration/configuration.service";
import {ResourceModule} from "../resource.module";
import {firstValueFrom, map, mergeMap, Observable, of, timer} from "rxjs";
import {HttpHeaders, HttpParams, HttpResponse} from "@angular/common/http";

export enum SyncMode {
    CREATE_IF_MISSING = <any>"CREATE_IF_MISSING",
    SHALLOW_SYNC = <any>"SHALLOW_SYNC",
    DEEP_SYNC = <any>"DEEP_SYNC"
}

export class SyncOptions {
    public syncMode: SyncMode = SyncMode.SHALLOW_SYNC;
    public syncMenuItems: boolean = true;
    public addNewSellablesAsNotSoldHere: boolean = false;
    public sourceIncludedTouchItemIds: number[];
    public sourceIncludedCategoryIds: number[];
}

export class EntitySyncRequest {
    public targetClientIds: string[] = [];
    public syncOptions: SyncOptions = new SyncOptions();

    constructor(public identifier?: string, public entityName?: string) {
    }
}

/**
 * Generic service for accessing an entity resource from the server.
 *
 * The #ENTITY_RESOURCE must be registered as a provider in the Angular dependency injection to specify the resource
 * name. This will form part of the URL path
 *
 * Errors are thrown as a #DataClientErrorMessage
 */
@Injectable()
export class EntityService {

    /**
     * Static URL prefix for all server side calls
     * @type {string}
     */
    public static readonly URL_PREFIX = '/dataserver/entity/';

    /**
     * Provider lookup key alias that is used during dependency injection
     * @type {InjectionToken}
     */
    public static readonly ENTITY_RESOURCE: InjectionToken<string> = new InjectionToken<string>('entityResource');

    /**
     * Provider lookup key alias that is used during dependency injection
     * @type {InjectionToken}
     */
    public static readonly RESOURCE_VERSION: InjectionToken<string> = new InjectionToken<string>('resourceVersion');

    /**
     * Static URL prefix for getting a pending process result
     * @type {string}
     */
    public static readonly URL_PENDING_PREFIX = '/dataserver/entity/pendingProcess/';

    /**
     * URL root to access the resource ending in '/'. Primary keys and child entities may need to be appended
     */
    protected readonly url: string;

    /**
     * Constructs the service with a resource, Http service, and Error service.
     * The resource may be injected via the #ENTITY_RESOURCE alias
     * @param http HTTP Service
     * @param configService
     * @param nameSpace NameSpace that forms part of the URL
     * @param entityNames Entity names for the APP
     * @param resource Resource name that forms part of the URL
     * @param version Resource version that forms part of the URL
     */
    constructor(protected http: JwtHttpClientService,
                protected configService: ConfigurationService,
                @Inject(ResourceModule.RESOURCE_NAMESPACE_KEY) protected nameSpace: string,
                @Inject(ResourceModule.ENTITY_NAMES_KEY) protected entityNames: string[],
                @Inject(EntityService.ENTITY_RESOURCE) protected resource: string,
                @Inject(EntityService.RESOURCE_VERSION) protected version: number,
                @Inject(ResourceModule.RESOURCE_USE_ASYNC) protected useAsync: boolean) {
        if (entityNames && entityNames.indexOf(resource) < 0) {
            throw new Error("Entity " + resource + " is not supported.");
        }

        let url: string = EntityService.URL_PREFIX + nameSpace + "/" + resource;
        if (version) {
            url = url + ".v" + version;
        }
        url = url + "/";

        this.url = url;
    }

    public getNameSpace(): string {
        return this.nameSpace;
    }

    public getResource(): string {
        return this.resource;
    }

    /**
     * Retrieves an entity by a unique identifier
     * @param identifier Primary Key
     * @param params
     * @returns {Promise<any>} JSON object representation of the entity
     */
    public getEntity(identifier: any, params?: { [key: string]: any }): Promise<Object> {
        return firstValueFrom(this.http.get(this.url + identifier, new HttpParams({fromObject: params})).pipe(
            map((response: HttpResponse<Object>) => response.body || {})
        ));
    }

    /**
     * Retrieves an entity by a unique identifier
     * @param identifier Primary Key
     * @returns {Promise<any>} JSON object representation of the entity
     */
    public cloneEntity(identifier: any): Promise<Object> {
        return firstValueFrom((this.useAsync
            ? this.handleAsync(this.http.get(this.url + identifier, new HttpParams({fromObject: {"clone": "true"}}), new HttpHeaders({"async": "true"})))
            : this.http.get(this.url + identifier, new HttpParams({fromObject: {"clone": "true"}}))).pipe(
            map((response: HttpResponse<Object>) => response.body || {})
        ));
    }

    /**
     * Retrieves a nested collection of an entity by a path
     * @returns {Promise<any>} JSON array representation of the collection
     * @param collectionPath
     */
    public getCollection(collectionPath: string): Promise<Object> {
        return firstValueFrom(this.http.get(this.url + collectionPath).pipe(
            map((response: HttpResponse<Object>) => response.body || {})
        ));
    }

    /**
     * Retrieves a nested component of an entity by a path
     * @returns {Promise<any>} JSON object representation of the component
     * @param componentPath
     */
    public getComponent(componentPath: string): Promise<Object> {
        return firstValueFrom(this.http.get(this.url + componentPath).pipe(
            map((response: HttpResponse<Object>) => response.body || {})
        ));
    }

    /**
     * Creates an entity
     * @param entityData
     * @returns {Promise<any>} JSON object representation of the saved entity
     */
    public createEntity(entityData: any): Promise<Object> {
        return firstValueFrom((this.useAsync
            ? this.handleAsync(this.http.post(this.url, entityData.toJSONString ? entityData.toJSONString() : entityData, null, new HttpHeaders({"async": "true"})))
            : this.http.post(this.url, entityData.toJSONString ? entityData.toJSONString() : entityData)).pipe(
            map((response: HttpResponse<Object>) => response.body || {})
        ));
    }

    /**
     * Updates an entity
     * @param entityData
     * @returns {Promise<any>} JSON object representation of the saved entity
     */
    public updateEntity(entityData: any): Promise<Object> {
        return firstValueFrom((this.useAsync
            ? this.handleAsync(this.http.put(this.url, entityData.toJSONString ? entityData.toJSONString() :  entityData, null, new HttpHeaders({"async": "true"})))
            : this.http.put(this.url, entityData.toJSONString ? entityData.toJSONString() :  entityData)).pipe(
            map((response: HttpResponse<Object>) => response.body || {})
        ));
    }

    private handleAsync(serviceObservable: Observable<HttpResponse<Object>>): Observable<HttpResponse<Object>> {
        return serviceObservable.pipe(
            mergeMap((response: HttpResponse<Object>) => response.status === 202 && response.headers.has("processId") ? this.getAsyncProcessResponse(response.headers.get("processId")) : of(response))
        );
    }

    private getAsyncProcessResponse(processId: string): Observable<HttpResponse<Object>> {
        return timer(5000).pipe(
            mergeMap(() => this.http.get(EntityService.URL_PENDING_PREFIX + processId)),
            mergeMap((response: HttpResponse<Object>) => response.status === 202 ? this.getAsyncProcessResponse(processId) : of(response))
        );
    }

    /**
     * Delete an entity
     * @param identifier
     * @returns {Promise<R>) returns no errors if successful
     */
    public deleteEntity(identifier: any): Promise<string> {
        return firstValueFrom(this.http.delete(this.url + identifier, new HttpParams({fromObject: {action: "delete"}})).pipe(
            map((response: HttpResponse<string>) => response.body || "")
        ));
    }

    /**
     * Discontinue an entity
     * @param identifier
     * @returns {Promise<R>) returns no errors if successful
     */
    public discontinueEntity(identifier: any): Promise<string> {
        return firstValueFrom(this.http.delete(this.url + identifier, new HttpParams({fromObject: {action: "discontinue"}})).pipe(
            map((response: HttpResponse<string>) => response.body || "")
        ));
    }

    /**
     * Recontinue an entity
     * @param identifier
     * @returns {Promise<any>) returns no errors if successful
     */
    public recontinueEntity(identifier: any): Promise<string> {
        return firstValueFrom(this.http.delete(this.url + identifier, new HttpParams({fromObject: {action: "recontinue"}})).pipe(
            map((response: HttpResponse<string>) => response.body || "")
        ));
    }

    /**
     * Initiates a sync of an entity
     * @param entitySyncRequest
     * @returns {Promise<any>) returns no errors if successful
     */
    public syncEntity(entitySyncRequest: EntitySyncRequest): Promise<Object> {
        return firstValueFrom(this.http.post(this.url + "sync", entitySyncRequest).pipe(
            map((response: HttpResponse<Object>) => response.body || {})
        ));
    }

    /**
     * Indicates that the authenticated user has permission to create the entity
     * @returns {Promise<boolean>}
     */
    public canCreateEntity(): Promise<boolean> {
        return EntityService.canCreateEntity(this.configService, this.nameSpace + "/" + this.resource);
    }

    /**
     * Indicates that the authenticated user has permission to retrieve the entity
     * @returns {Promise<boolean>}
     */
    public canGetEntity(): Promise<boolean> {
        return EntityService.canGetEntity(this.configService, this.nameSpace + "/" + this.resource);
    }

    /**
     * Indicates that the authenticated user has permission to update the entity
     * @returns {Promise<boolean>}
     */
    public canUpdateEntity(): Promise<boolean> {
        return EntityService.canUpdateEntity(this.configService, this.nameSpace + "/" + this.resource);
    }

    /**
     * Indicates that the authenticated user has permission to delete the entity
     * @returns {Promise<boolean>}
     */
    public canDeleteEntity(): Promise<boolean> {
        return EntityService.canDeleteEntity(this.configService, this.nameSpace + "/" + this.resource);
    }

    /**
     * Indicates that the authenticated user has permission to discontinue/recontinue the entity
     * @returns {Promise<boolean>}
     */
    public canDiscontinueEntity(): Promise<boolean> {
        return EntityService.canDiscontinueEntity(this.configService, this.nameSpace + "/" + this.resource);
    }

    /**
     * Indicates that the authenticated user has permission to sync the entity to another instance in the group
     * @returns {Promise<boolean>}
     */
    public canSyncEntity(): Promise<boolean> {
        return EntityService.canSyncEntity(this.configService, this.nameSpace + "/" + this.resource);
    }


    public static canCreateEntity(configService: ConfigurationService, resource: string): Promise<boolean> {
        return configService.getString("resourcePrivilege/entity/" + resource).then((privMask: string) => privMask ? configService.getBoolean("authorisation/" + privMask.replace("*", "create")) : false);
    }

    public static canGetEntity(configService: ConfigurationService, resource: string): Promise<boolean> {
        return configService.getString("resourcePrivilege/entity/" + resource).then((privMask: string) => privMask ? configService.getBoolean("authorisation/" + privMask.replace("*", "view")) : false);
    }

    public static canUpdateEntity(configService: ConfigurationService, resource: string): Promise<boolean> {
        return configService.getString("resourcePrivilege/entity/" + resource).then((privMask: string) => privMask ? configService.getBoolean("authorisation/" + privMask.replace("*", "update")) : false);
    }

    public static canDeleteEntity(configService: ConfigurationService, resource: string): Promise<boolean> {
        return configService.getString("resourcePrivilege/entity/" + resource).then((privMask: string) => privMask ? configService.getBoolean("authorisation/" + privMask.replace("*", "delete")) : false);
    }

    public static canDiscontinueEntity(configService: ConfigurationService, resource: string): Promise<boolean> {
        return configService.getString("resourcePrivilege/entity/" + resource).then((privMask: string) => privMask ? configService.getBoolean("authorisation/" + privMask.replace("*", "update")) : false);
    }

    public static canSyncEntity(configService: ConfigurationService, resource: string): Promise<boolean> {
        return configService.getString("resourcePrivilege/entity/" + resource).then((privMask: string) => privMask ? configService.getBoolean("authorisation/" + privMask.replace("*", "sync")) : false);
    }
}
